import { Trophy } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { SortConfig, getSortIcon } from "src/helpers/api/sortconfig";

interface Record {
  symbol: string;
  date: string;
  indicesname?: string;
  close: number;
  peak_return: number;
  current_return: number;
}

export type PerformanceTab = "all" | "top";

interface PastPerformanceTableProps {
  records: Record[];
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isLoading?: boolean;
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  onExport?: () => void;
  isExporting?: boolean;
  activeTab: PerformanceTab;
  onTabChange: (tab: PerformanceTab) => void;
  topPerformerCount: number;
}

const PastPerformanceTable: React.FC<PastPerformanceTableProps> = ({
  records,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isLoading,
  sortConfig,
  onSort,
  onExport,
  isExporting,
  activeTab,
  onTabChange,
  topPerformerCount,
}) => {
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const [tableSearch] = useState("");
  const filteredRecords = useMemo(() => {
    if (!tableSearch) return records;
    const query = tableSearch.toLowerCase();
    return records.filter((r) => r.symbol.toLowerCase().includes(query));
  }, [records, tableSearch]);

  // A new sort (or tab) starts a fresh result set; leaving the scroll position
  // where it was would land mid-list and look like the sort did nothing.
  useEffect(() => {
    tableContainerRef.current?.scrollTo({ top: 0 });
  }, [sortConfig.key, sortConfig.direction, activeTab]);

  const handleScroll = () => {
    // The Top Performers tab is a complete, unpaginated list.
    if (activeTab === "top") return;
    if (!tableContainerRef.current || !onLoadMore || !hasMore || isLoadingMore)
      return;

    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      onLoadMore();
    }
  };

  const tabs: { key: PerformanceTab; label: string; count?: number }[] = [
    { key: "all", label: "All Stocks" },
    { key: "top", label: "Top Performers", count: topPerformerCount },
  ];

  return (
    <div className="flex-2 bg-white dark:bg-[#121419] overflow-hidden flex flex-col h-full">
      <div className="px-1 py-1 flex items-center justify-between shrink-0 gap-3">
        <div className="flex items-center gap-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => onTabChange(tab.key)}
                aria-pressed={isActive}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-sm font-medium transition-colors cursor-pointer ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    : "text-gray-500 hover:text-emerald-500 dark:text-gray-400"
                }`}
              >
                {tab.key === "top" && <Trophy className="h-3.5 w-3.5" />}
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span
                    className={`rounded-full px-1.5 text-[10px] font-bold leading-4 ${
                      isActive
                        ? "bg-emerald-500 text-white"
                        : "bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3 ml-auto">
          {/* <div className="relative">
            <input
              type="text"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              placeholder="Search stocks..."
              className="h-8 w-32 sm:w-48 pl-8 pr-3 bg-gray-50 dark:bg-[#151921] border border-gray-200 dark:border-[#1E232E] rounded-md text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:border-emerald-500/50 transition-all duration-300"
            />
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          </div> */}
          <button
            onClick={onExport}
            disabled={isExporting || isLoading || records.length === 0}
            title={
              records.length === 0
                ? "Nothing to export for the current filters"
                : "Download the full filtered result set as an Excel workbook"
            }
            className="text-xs font-medium text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 transition-colors group whitespace-nowrap cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isExporting ? (
              <span className="inline-block w-3.5 h-3.5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg
                className="w-3.5 h-3.5 group-hover:translate-y-[1px] transition-transform"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            )}
            <span>{isExporting ? "Exporting..." : "Export Excel"}</span>
          </button>
        </div>
      </div>

      <div
        ref={tableContainerRef}
        onScroll={handleScroll}
        className="relative min-h-0 flex-1 overflow-auto"
      >
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#f4f5f8] dark:bg-[#30324e]">
              <th
                className="py-1.5 px-2 text-left text-xs font-semibold text-[#3D3D3D] dark:text-white tracking-wider cursor-pointer group"
                onClick={() => onSort("symbol")}
              >
                <div className="relative flex items-center w-max pr-4">
                  Symbol
                  {getSortIcon(sortConfig, "symbol")}
                </div>
              </th>
              <th
                className="py-1.5 px-2 text-left text-xs font-semibold text-[#3D3D3D] dark:text-white tracking-wider cursor-pointer group"
                onClick={() => onSort("date")}
              >
                <div className="relative flex items-center w-max pr-4">
                  Date
                  {getSortIcon(sortConfig, "date")}
                </div>
              </th>
              <th
                className="py-1.5 px-2 text-left text-xs font-semibold text-[#3D3D3D] dark:text-white tracking-wider max-w-[150px] cursor-pointer group"
                onClick={() => onSort("indicesname")}
              >
                <div className="relative flex items-center w-max pr-4">
                  Stock Universe
                  {getSortIcon(sortConfig, "indicesname")}
                </div>
              </th>
              <th
                className="py-1.5 px-2 text-right text-xs font-semibold text-[#3D3D3D] dark:text-white tracking-wider whitespace-nowrap cursor-pointer group"
                onClick={() => onSort("close")}
              >
                <div className="relative flex items-center justify-end w-full pr-4">
                  Close Price{getSortIcon(sortConfig, "close")}
                </div>
              </th>
              <th
                className="py-1.5 px-2 text-right text-xs font-semibold text-[#3D3D3D] dark:text-white tracking-wider whitespace-nowrap cursor-pointer group"
                onClick={() => onSort("peak_return")}
              >
                <div className="relative flex items-center justify-end w-full pr-4">
                  Peak Return %{getSortIcon(sortConfig, "peak_return")}
                </div>
              </th>
              <th
                className="py-1.5 px-2 text-right text-xs font-semibold text-[#3D3D3D] dark:text-white tracking-wider whitespace-nowrap cursor-pointer group"
                onClick={() => onSort("current_return")}
              >
                <div className="relative flex items-center justify-end w-full pr-4">
                  Current Return %{getSortIcon(sortConfig, "current_return")}
                </div>
              </th>
            </tr>
          </thead>

          <tbody
            className={`divide-y divide-gray-100 dark:divide-gray-800 transition-opacity duration-200 ${
              isLoading && filteredRecords.length > 0
                ? "opacity-60"
                : "opacity-100"
            }`}
          >
            {isLoading && filteredRecords.length === 0 ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="animate-pulse">
                  <td className="px-2 py-2">
                    <div className="h-4 w-20 bg-gray-100 dark:bg-white/5 rounded" />
                  </td>
                  <td className="px-2 py-2">
                    <div className="h-4 w-24 bg-gray-100 dark:bg-white/5 rounded" />
                  </td>
                  <td className="px-2 py-2">
                    <div className="h-4 w-24 bg-gray-100 dark:bg-white/5 rounded" />
                  </td>
                  <td className="px-2 py-2">
                    <div className="h-4 w-16 bg-gray-100 dark:bg-white/5 rounded ml-auto" />
                  </td>
                  <td className="px-2 py-2">
                    <div className="h-4 w-16 bg-gray-100 dark:bg-white/5 rounded ml-auto" />
                  </td>
                  <td className="px-2 py-2">
                    <div className="h-6 w-16 bg-gray-100 dark:bg-white/5 rounded ml-auto" />
                  </td>
                </tr>
              ))
            ) : filteredRecords.length > 0 ? (
              filteredRecords.map((record, idx) => (
                <tr
                  key={`${record.symbol}-${idx}`}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors border-b border-gray-100 dark:border-gray-800"
                >
                  <td className="px-2 py-1 font-medium text-gray-900 dark:text-white">
                    {record.symbol}
                  </td>
                  <td className="px-2 py-1 text-gray-600 dark:text-gray-400 font-medium">
                    {record.date}
                  </td>
                  <td className="px-2 py-1 text-gray-600 dark:text-gray-400 font-medium max-w-[150px] break-words leading-tight">
                    {record.indicesname || "-"}
                  </td>
                  <td className="px-2 py-1 text-right text-gray-900 dark:text-white font-medium tabular-nums">
                    ₹{record.close.toLocaleString()}
                  </td>
                  <td
                    className={`px-2 py-1 text-right font-medium whitespace-nowrap tabular-nums ${record.peak_return > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}
                  >
                    {record.peak_return > 0
                      ? `+${record.peak_return}%`
                      : `${record.peak_return}%`}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold tabular-nums ${
                        record.current_return >= 0
                          ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400"
                          : "bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400"
                      }`}
                    >
                      {record.current_return >= 0 ? "+" : ""}
                      {record.current_return}%
                    </span>
                  </td>
                </tr>
              ))
            ) : !isLoadingMore ? (
              <tr>
                <td colSpan={6} className="py-20 text-center">
                  <p className="text-gray-400 dark:text-gray-500 text-sm">
                    {activeTab === "top"
                      ? "No stock cleared the top-performer threshold for this range"
                      : "No matching records found"}
                  </p>
                </td>
              </tr>
            ) : null}
            {isLoadingMore && (
              <tr>
                <td colSpan={6} className="py-4 text-center">
                  <div className="inline-block w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PastPerformanceTable;
